import { useCallback } from 'react';
import usePDA1090_R01Store from "./usePDA1090_R01Store";
import { getPDA1090_R01, updatePDA1090_R01 } from "src/containers/PDA/PDA1090_R01/pda1090Api";
import { useToast } from "src/hooks/useToast";
import { PDA1090_R01UpdateRequest, StockInfo } from "./PDA1090_R01Dto";

/**
 * PDA1090_R01 Content Hook
 * 
 * Container에서 사용할 비즈니스 로직을 제공합니다.
 * - LOT 스캔 → 재고 현황 조회
 * - 창고 선택
 * - 재고 조정 저장
 */
export const usePDA1090_R01Content = () => {
  const { 
    lotNo,
    setLotNo,
    actualStock,
    setActualStock,
    data,
    setData,
    selectedStock,
    setSelectedStock,
    setLoading,
    reset,
    resetForm,
  } = usePDA1090_R01Store();
  
  const { showToast } = useToast();

  /**
   * LOT 스캔 - 재고 현황 조회
   */
  const handleScanLot = useCallback(async (scanLotNo?: string) => {
    const targetLotNo = scanLotNo || lotNo;

    if (!targetLotNo.trim()) {
      showToast("error", "LOT 번호를 입력하세요.");
      return;
    }

    try {
      setLoading(true);
      
      const response = await getPDA1090_R01(targetLotNo.trim());
      const lotData = response?.data?.data;
      
      if (!lotData) {
        throw new Error("LOT 정보를 찾을 수 없습니다.");
      }
      
      setData(lotData);
      setLotNo(""); // LOT 입력 초기화
      setActualStock(null); // 실재고 초기화
      showToast("success", `LOT 스캔 완료 (${lotData.itemName})`);
      
    } catch (error: any) {
      console.error("LOT 스캔 실패:", error);
      showToast("error", error?.response?.data?.message || "LOT 스캔에 실패했습니다.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [lotNo, setLoading, setData, setLotNo, setActualStock, showToast]);


  /**
   * 창고 선택 (그리드에서 행 클릭)
   */
  const handleSelectStock = useCallback((stock: StockInfo) => {
    setSelectedStock(stock);
    setActualStock(null); // 실재고 초기화
  }, [setSelectedStock, setActualStock]);

  /**
   * 재고 조정 저장
   */
  const handleSaveAdjustment = useCallback(async () => {
    // 유효성 검증
    if (!data) {
      showToast("error", "LOT 정보가 없습니다. LOT를 먼저 스캔하세요.");
      return;
    }

    if (!selectedStock) {
      showToast("error", "창고를 선택하세요.");
      return;
    }

    if (actualStock === null || actualStock === undefined) {
      showToast("error", "실재고를 입력하세요.");
      return;
    }

    if (actualStock < 0) {
      showToast("error", "실재고는 0보다 작을 수 없습니다.");
      return;
    }

    if (actualStock === selectedStock.stockQty) {
      showToast("warning", "현재고와 실재고가 동일합니다.");
      return;
    }

    try {
      setLoading(true);

      const request: PDA1090_R01UpdateRequest = {
        lotNo: data.lotNo,
        itemCd: data.itemCd,
        warehouseCd: selectedStock.warehouseCd,
        itemStatus: selectedStock.itemStatus,
        currentStock: selectedStock.stockQty,
        actualStock: actualStock,
        adjustReason: "재고실사",
        remark: "",
      };

      const response = await updatePDA1090_R01(request);
      const result = response?.data?.data;

      if (result) {
        const diffQty = result.diffQty || 0;
        const sign = diffQty >= 0 ? "+" : "";
        showToast(
          "success", 
          `재고 조정 완료 (${sign}${diffQty.toLocaleString()}) - 전표: ${result.transportNo}`
        );

        // Info·Table 초기화 후 다음 LOT 스캔 대기
        reset();
      }

    } catch (error: any) {
      showToast("error", error.response.data.message);
    } finally {
      setLoading(false);
    }
  }, [data, selectedStock, actualStock, setLoading, showToast, reset]);

  /**
   * 취소 (입력 초기화)
   */
  const handleCancel = useCallback(() => {
    setActualStock(null);
    showToast("info", "입력이 초기화되었습니다.");
  }, [setActualStock, showToast]);

  /**
   * Enter 키 이벤트 핸들러
   */
  const handleLotNoEnter = useCallback(() => {
    handleScanLot();
  }, [handleScanLot]);

  const handleActualStockEnter = useCallback(() => {
    handleSaveAdjustment();
  }, [handleSaveAdjustment]);

  return {
    handleScanLot,
    handleSelectStock,
    handleSaveAdjustment,
    handleCancel,
    handleLotNoEnter,
    handleActualStockEnter,
  };
};
